import React, { useState, useEffect, useCallback } from 'react';
import { useFiliale } from '../context/FilialeContext';
import http from '../api/http';
import BlueHeader from '../components/BlueHeader';
import { Plus, Edit, Trash2, RefreshCw, Search } from 'lucide-react';

const PersonnelStc = () => {
  const { filialeId } = useFiliale();
  const [stcList, setStcList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [personnel, setPersonnel] = useState([]);
  const [periodesPaie, setPeriodesPaie] = useState([]);
  const [filters, setFilters] = useState({
    personnel_id: '',
    paie_id: '',
    integre_paie: ''
  });
  const [formData, setFormData] = useState({
    personnel_id: '',
    paie_id: '',
    integre_paie: false
  });

  const loadDropdownData = useCallback(async () => {
    try {
      const [persRes, periodesRes, periodesInterimRes] = await Promise.all([
        http.get('/api/personnel').catch(() => ({ data: [] })),
        http.get('/api/paies').catch(() => ({ data: [] })),
        http.get('/api/paies?isInterim=true').catch(() => ({ data: [] }))
      ]);
      console.log('Personnel raw:', persRes.data);
      console.log('Périodes normales:', periodesRes.data);
      console.log('Périodes intérim:', periodesInterimRes.data);
      
      // Récupérer les employés selon la structure de réponse
      let personnelData = [];
      if (Array.isArray(persRes.data)) {
        personnelData = persRes.data;
      } else if (persRes.data && persRes.data.data && Array.isArray(persRes.data.data)) {
        personnelData = persRes.data.data;
      } else if (persRes.data && typeof persRes.data === 'object') {
        personnelData = Object.values(persRes.data).filter(item => typeof item === 'object' && item.idpersonnel);
      }
      
      // Filtrer les employés par filiale si nécessaire
      if (filialeId && filialeId !== 'all') {
        personnelData = personnelData.filter(p => p.filiale_id === parseInt(filialeId));
      }
      console.log('Personnel filtré:', personnelData);
      setPersonnel(personnelData);
      
      // Filtrer les périodes normales pour ne garder que la période ouverte de la filiale
      let periodesData = Array.isArray(periodesRes.data) ? periodesRes.data : [];
      if (filialeId && filialeId !== 'all') {
        periodesData = periodesData.filter(p => p.filiale_id === parseInt(filialeId) && p.etat === 1);
      } else {
        periodesData = periodesData.filter(p => p.etat === 1);
      }
      console.log('Périodes normales filtrées:', periodesData);
      
      // Filtrer les périodes intérim pour ne garder que la période ouverte de la filiale
      let periodesInterimData = Array.isArray(periodesInterimRes.data) ? periodesInterimRes.data : [];
      if (filialeId && filialeId !== 'all') {
        periodesInterimData = periodesInterimData.filter(p => p.filiale_id === parseInt(filialeId) && p.etat === 1);
      } else {
        periodesInterimData = periodesInterimData.filter(p => p.etat === 1);
      }
      console.log('Périodes intérim filtrées:', periodesInterimData);
      
      // Combiner les deux types de périodes avec un indicateur
      const allPeriodes = [
        ...periodesData.map(p => ({ ...p, type: 'normal', id: p.idpaie, libelle_paie: p.libelle_paie })),
        ...periodesInterimData.map(p => ({ ...p, type: 'interim', id: p.idpaie_interime, libelle_paie: p.libelle_paie }))
      ];
      
      setPeriodesPaie(allPeriodes);
    } catch (error) {
      console.error('Erreur chargement dropdown:', error);
    }
  }, [filialeId]);

  const fetchStc = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.personnel_id) params.personnel_id = filters.personnel_id;
      if (filters.paie_id) params.paie_id = filters.paie_id;
      if (filters.integre_paie !== '') params.integre_paie = filters.integre_paie;
      if (filialeId && filialeId !== 'all') params.filiale_id = filialeId;

      const response = await http.get('/api/personnel-stc', { params });
      setStcList(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement STC:', error);
      setStcList([]);
    } finally {
      setLoading(false);
    }
  }, [filters.personnel_id, filters.paie_id, filters.integre_paie, filialeId]);

  useEffect(() => {
    loadDropdownData();
  }, [loadDropdownData]);

  useEffect(() => {
    fetchStc();
  }, [fetchStc]);

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setFormData({ ...formData, personnel_id: employee.idpersonnel });
    setShowEmployeeModal(false);
    
    // La période sera sélectionnée manuellement par l'utilisateur
    // La détection automatique du type de paie sera faite lors du calcul de paie
  };

  const handleCreate = () => {
    setEditingItem(null);
    setSelectedEmployee(null);
    setFormData({
      personnel_id: '',
      paie_id: '',
      integre_paie: false
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const employee = personnel.find(p => p.idpersonnel === item.personnel_id);
    setSelectedEmployee(employee || null);
    setFormData({
      personnel_id: item.personnel_id || '',
      paie_id: item.paie_id || '',
      integre_paie: item.integre_paie === 1
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce STC ?')) return;
    try {
      await http.delete(`/api/personnel-stc/${id}`);
      fetchStc();
      alert('STC supprimé avec succès');
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await http.put(`/api/personnel-stc/${editingItem.id}`, formData);
        alert('STC modifié avec succès');
      } else {
        await http.post('/api/personnel-stc', formData);
        alert('STC créé avec succès');
      }
      setShowModal(false);
      fetchStc();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert(error.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-gray-500">Chargement...</div>
      </div>
    );
  }

  const filteredStcList = stcList.filter(stc =>
    stc.personnel_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stc.personnel_prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stc.personnel_matricule?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Configuration Solde de Tout Compte (STC)"
        subtitle="Gérer les soldes de tout compte du personnel"
        actions={[
          {
            label: 'Ajouter',
            icon: Plus,
            onClick: handleCreate,
            primary: true
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchStc,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        {/* Filtres */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employé</label>
              <select
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                value={filters.personnel_id}
                onChange={(e) => setFilters({ ...filters, personnel_id: e.target.value })}
              >
                <option value="">Tous</option>
                {personnel.map(p => (
                  <option key={p.idpersonnel} value={p.idpersonnel}>
                    {p.nom} {p.prenom}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Période de paie</label>
              <select
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                value={filters.paie_id}
                onChange={(e) => setFilters({ ...filters, paie_id: e.target.value })}
              >
                <option value="">Toutes</option>
                {periodesPaie.map(p => (
                  <option key={`${p.type}-${p.idpaie || p.idpaie_interime}`} value={p.idpaie || p.idpaie_interime}>
                    {p.type === 'interim' ? '[INTÉRIM] ' : ''}{p.libelle_paie}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Intégré à la paie</label>
              <select
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                value={filters.integre_paie}
                onChange={(e) => setFilters({ ...filters, integre_paie: e.target.value })}
              >
                <option value="">Tous</option>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </select>
            </div>
          </div>
        </div>

        {/* Recherche */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom ou matricule..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Tableau */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Liste des STC</h3>
            <p className="text-sm text-gray-500 mt-1">{filteredStcList.length} STC configuré(s)</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employé</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Période de paie</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Intégré à la paie</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date création</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredStcList.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      Aucun STC configuré
                    </td>
                  </tr>
                ) : (
                  filteredStcList.map((stc) => (
                    <tr key={stc.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        <div>{stc.personnel_nom} {stc.personnel_prenom}</div>
                        <div className="text-sm text-gray-500">{stc.personnel_matricule}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>{stc.paie_libelle || '-'}</div>
                        <div className="text-sm text-gray-500">
                          {stc.paie_date_debut ? stc.paie_date_debut.split('T')[0] : '-'} au {stc.paie_date_fin ? stc.paie_date_fin.split('T')[0] : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          stc.integre_paie === 1
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {stc.integre_paie === 1 ? 'Oui' : 'Non'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {stc.created_at ? stc.created_at.split('T')[0] : '-'}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEdit(stc)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(stc.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal principal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl border border-blue-100 flex flex-col max-h-[90vh]">
            <div className="mb-4 flex items-center justify-between border-b border-blue-100 pb-4 px-6">
              <h3 className="text-lg font-bold text-blue-900">
                {editingItem ? 'Modifier' : 'Nouveau'} STC
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-900">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Employé <span className="text-red-500">*</span></label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      required
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 focus:outline-none"
                      value={selectedEmployee ? `${selectedEmployee.nom} ${selectedEmployee.prenom}` : ''}
                      placeholder="Sélectionner un employé"
                      onClick={() => setShowEmployeeModal(true)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowEmployeeModal(true)}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Chercher
                    </button>
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Période de paie <span className="text-red-500">*</span></label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    value={formData.paie_id}
                    onChange={(e) => setFormData({ ...formData, paie_id: e.target.value })}
                  >
                    <option value="">Sélectionner</option>
                    {periodesPaie.map(p => (
                      <option key={`${p.type}-${p.id}`} value={p.id}>
                        {p.type === 'interim' ? '[INTÉRIM] ' : ''}{p.libelle_paie}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="integre_paie"
                    checked={formData.integre_paie}
                    onChange={(e) => setFormData({ ...formData, integre_paie: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="integre_paie" className="ml-2 block text-sm text-gray-900">
                    Intégré au calcul de paie
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 border-t border-blue-100 p-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingItem ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de recherche d'employés */}
      {showEmployeeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl border border-blue-100 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="mb-4 flex items-center justify-between border-b border-blue-100 pb-4 px-6">
              <h3 className="text-lg font-bold text-blue-900">Sélectionner un employé</h3>
              <button onClick={() => setShowEmployeeModal(false)} className="text-gray-500 hover:text-gray-900">✕</button>
            </div>
            <div className="p-6 flex-1 flex flex-col overflow-hidden">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="Rechercher par nom ou prénom..."
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                />
              </div>
              <div className="flex-1 overflow-y-auto">
                {personnel
                  .filter(p =>
                    p.nom.toLowerCase().includes(employeeSearch.toLowerCase()) ||
                    p.prenom.toLowerCase().includes(employeeSearch.toLowerCase())
                  )
                  .map(p => (
                    <div
                      key={p.idpersonnel}
                      onClick={() => handleEmployeeSelect(p)}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                    >
                      <div className="font-medium text-gray-900">
                        {p.nom} {p.prenom}
                      </div>
                      <div className="text-sm text-gray-500">
                        {p.matricule || 'N/A'}
                      </div>
                    </div>
                  ))}
                {personnel.filter(p =>
                  p.nom.toLowerCase().includes(employeeSearch.toLowerCase()) ||
                  p.prenom.toLowerCase().includes(employeeSearch.toLowerCase())
                ).length === 0 && (
                  <div className="px-4 py-2 text-sm text-gray-500">Aucun employé trouvé</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonnelStc;
